import React, { useEffect, useState } from 'react';
import { ArrowUpRight, ShoppingBag, Check, Download, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import api, { mediaUrl } from '../lib/api';
import SmartImage from './SmartImage';
import { useCart } from '../context/CartContext';

const EcoStore = () => {
  const { addItem } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [added, setAdded] = useState({});

  useEffect(() => {
    api.get('/shop/products')
      .then((res) => setProducts((res.data || []).slice(0, 3)))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const handleAdd = (p) => {
    addItem(p, 1);
    setAdded((prev) => ({ ...prev, [p.id]: true }));
    setTimeout(() => setAdded((prev) => ({ ...prev, [p.id]: false })), 1600);
  };

  // Hide the whole section when there are genuinely no products (never show placeholders).
  if (!loading && products.length === 0) return null;

  return (
    <section id="shop" className="relative py-20 md:py-28 bg-cream overflow-hidden" data-testid="eco-store-section">
      {/* Decorative background floral */}
      <div className="pointer-events-none absolute -right-20 top-40 w-[600px] h-[600px] opacity-20">
        <FloralPetals />
      </div>

      <div className="relative max-w-[1300px] mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="w-8 h-8 rounded-full flex items-center justify-center bg-rose-soft">
              <span className="w-3 h-3 rounded-full bg-mahogany" />
            </span>
            <span className="eyebrow">Our Eco-Store</span>
          </div>
          <h2 className="font-serif-display text-burgundy text-4xl md:text-6xl leading-[1.05]">
            Bring <span className="font-italic-display text-rose-italic">the Retreat</span> Home
          </h2>
          <p className="mt-5 text-burgundy/80">Curated tools to support your healing journey — naturally.</p>
        </div>

        {loading ? (
          <div className="flex justify-center mt-16"><Loader2 className="w-8 h-8 animate-spin text-mahogany" /></div>
        ) : (
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="eco-store-grid">
            {products.map((p, i) => (
              <article key={p.id} className="group fade-up" style={{ animationDelay: `${i * 90}ms` }} data-testid={`eco-product-${p.slug}`}>
                <Link to={`/product/${p.slug}`} className="relative block product-image-frame aspect-[3/4] bg-cream-2 rounded-2xl overflow-hidden">
                  <SmartImage src={mediaUrl(p.image)} alt={p.title} className="w-full h-full">
                    <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                      {p.category && (
                        <span className="text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-white/80 text-mahogany font-medium">{p.category}</span>
                      )}
                      {p.product_type === 'digital' && (
                        <span className="text-[11px] uppercase tracking-widest px-2.5 py-1.5 rounded-full bg-mahogany text-cream flex items-center gap-1">
                          <Download className="w-3 h-3" /> Digital
                        </span>
                      )}
                    </div>
                  </SmartImage>
                </Link>
                <div className="mt-5 flex items-start justify-between gap-4">
                  <Link to={`/product/${p.slug}`}>
                    <h3 className="font-serif-display text-burgundy text-2xl leading-tight hover:text-rose-italic transition-colors">{p.title}</h3>
                  </Link>
                  <span className="text-mahogany text-lg font-medium whitespace-nowrap">${(p.price || 0).toFixed(2)}</span>
                </div>
                <button onClick={() => handleAdd(p)} className="mt-4 pill-btn" data-testid={`eco-add-to-cart-${p.slug}`}>
                  {added[p.id] ? <>Added <Check className="w-4 h-4" /></> : <>Add to Cart <ShoppingBag className="w-4 h-4" /></>}
                </button>
              </article>
            ))}
          </div>
        )}

        <div className="mt-14 text-center">
          <Link to="/shop" className="pill-btn" data-testid="eco-store-view-all">Discover all Products <ArrowUpRight className="w-4 h-4" /></Link>
        </div>
      </div>
    </section>
  );
};

const FloralPetals = () => (
  <svg viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg" fill="#F5C7CD">
    {Array.from({ length: 16 }).map((_, i) => (
      <ellipse key={i} cx="200" cy="90" rx="22" ry="70" transform={`rotate(${i * 22.5} 200 200)`} />
    ))}
    <circle cx="200" cy="200" r="22" fill="#EEB1BA" />
  </svg>
);

export default EcoStore;
